import React, { useCallback, useEffect, useState } from 'react'
import { API_ENDPOINTS } from '../../config/api'
import { getStoredAuthToken } from '../../api/client'
import { resolveColor } from '../../styles/colorContrast'
import { totalPoints, formatPoints } from '../../utils/score'
import type { GroupResult } from '../../types'

interface HistoryEntry {
  id: string
  submitted_at: string
  score?: number | null
  group_results?: GroupResult[] | null
  overall_percentage?: number | null
  geo?: { country?: string; region?: string } | null
  context?: { event?: string | null; description?: string | null } | null
}

interface Props {
  surveyId: string
  /** Called after a delete so the caller can refresh anything it also shows. */
  onChanged?: () => void
}

function formatDate(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value.slice(0, 10)
  return d.toISOString().slice(0, 10)
}

/**
 * Request 3: the respondent's own result history, one collapsed card per test,
 * expandable to the per-group breakdown. The header carries the context label
 * (request 4), the country and the date, so a card reads as a milestone.
 */
const MyResultsHistory: React.FC<Props> = ({ surveyId, onChanged }) => {
  const [entries, setEntries] = useState<HistoryEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const token = getStoredAuthToken()
      const res = await fetch(API_ENDPOINTS.surveys.myResponses(surveyId), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new Error(`Failed to load history (${res.status})`)
      const data: HistoryEntry[] = await res.json()
      setEntries(data)
      // Newest is open by default, the rest collapsed (Jerome's "version resumee").
      if (data.length > 0) setExpanded({ [data[0].id]: true })
    } catch (e: any) {
      setError(e?.message || 'Failed to load history')
    } finally {
      setLoading(false)
    }
  }, [surveyId])

  useEffect(() => {
    load()
  }, [load])

  const toggle = (id: string) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))

  const remove = async (id: string) => {
    if (!window.confirm('Delete this result? This cannot be undone.')) return
    const token = getStoredAuthToken()
    const res = await fetch(API_ENDPOINTS.surveys.myResponseById(surveyId, id), {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (res.ok) {
      setEntries((prev) => prev.filter((e) => e.id !== id))
      onChanged?.()
    } else {
      setError('Could not delete that result. Please try again.')
    }
  }

  if (loading) return <div className="text-foreground-muted">Loading your history...</div>
  if (error) return <div className="text-error">{error}</div>
  if (entries.length === 0) {
    return <div className="text-foreground-muted">You have no saved results yet.</div>
  }

  return (
    <div className="w-full max-w-3xl mx-auto space-y-6">
      <h2 className="text-2xl font-bold text-primary">My Happiness Index History</h2>

      {entries.map((entry) => {
        const groups = entry.group_results || []
        const isOpen = expanded[entry.id] ?? false
        const points = groups.length > 0 ? totalPoints(groups) : entry.score ?? 0

        return (
          <div key={entry.id} data-testid="history-entry" className="border border-border rounded-xl overflow-hidden">
            {/* Header: context label, country, date, delete */}
            <div className="flex items-center gap-3 px-4 py-3 bg-background-alt">
              <button
                type="button"
                onClick={() => toggle(entry.id)}
                aria-expanded={isOpen}
                className="flex-1 min-w-0 flex items-center gap-3 text-left"
              >
                <svg
                  className={`w-4 h-4 flex-shrink-0 text-foreground-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                <span className="font-medium text-foreground truncate">
                  {entry.context?.event || 'Happiness Index Test'}
                </span>
              </button>
              <span className="text-sm text-foreground-muted hidden sm:inline flex-shrink-0">
                {entry.geo?.country || ''}
              </span>
              <span className="text-sm text-foreground-muted flex-shrink-0">
                {formatDate(entry.submitted_at)}
              </span>
              <button
                type="button"
                onClick={() => remove(entry.id)}
                aria-label="Delete this result"
                className="p-1 rounded text-foreground-muted hover:text-error flex-shrink-0"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>

            {isOpen && (
              <div className="flex flex-col sm:flex-row">
                {/* Per-group bars, percentages kept per Jerome's mockup */}
                <div className="flex-1 p-4 space-y-3">
                  {groups.length === 0 && (
                    <p className="text-sm text-foreground-muted">No breakdown available for this result.</p>
                  )}
                  {groups.map((g) => {
                    const hex = resolveColor(g.color, '#6B7280')
                    const pct = Math.round(g.percentage)
                    return (
                      <div key={g.group_id} className="flex items-center gap-3">
                        <span className="text-sm font-medium w-40 flex-shrink-0 truncate" style={{ color: hex }}>
                          {g.group_name}
                        </span>
                        <span className="text-sm w-12 flex-shrink-0" style={{ color: hex }}>{pct}%</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2.5">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: hex }} />
                        </div>
                      </div>
                    )
                  })}
                  {entry.context?.description && (
                    <p className="text-sm text-foreground-muted italic pt-2 border-t border-border">
                      {entry.context.description}
                    </p>
                  )}
                </div>

                {/* Total points, not a percentage (request 1) */}
                <div className="sm:w-44 flex items-center justify-center p-4 bg-background-alt">
                  <span className="text-4xl font-bold text-primary" data-testid="history-points">
                    {formatPoints(points)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default MyResultsHistory
